// ============================================================================
// FNE-BRIDGE Frontend - Application principale avec routing
// ============================================================================

import React, { Suspense, lazy, useMemo } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useTranslation } from 'react-i18next';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/fr';
import { useThemeMode } from './hooks/useThemeMode';
import { GlobalSnackbar } from './components/common/AppAlert';
import { Spinner } from './components/common/AppLoader';
import { ProtectedRoute, PublicRoute } from './components/guards/AuthGuard';
import { AuthLayout } from './layouts/AuthLayout';
import { MainLayout } from './layouts/MainLayout';
import { PUBLIC_ROUTES, PROTECTED_ROUTES } from './config/routes';

// ============================================================================
// Lazy-loaded pages
// ============================================================================

// Auth
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const CreateAccountPage = lazy(() => import('./pages/auth/CreateAccountPage'));

// Dashboard
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));

// Stickers
const StickersPage = lazy(() => import('./pages/stickers/StickersPage'));

// Factures / Reçus
const InvoiceListPage = lazy(() => import('./pages/invoices/InvoiceListPage'));
const InvoiceCreatePage = lazy(() => import('./pages/invoices/InvoiceCreatePage'));
const InvoiceDetailPage = lazy(() => import('./pages/invoices/InvoiceDetailPage'));
const InvoiceImportPage = lazy(() => import('./pages/invoices/InvoiceImportPage'));
const InvoiceRefundPage = lazy(() => import('./pages/invoices/InvoiceRefundPage'));
const ReceivedInvoiceListPage = lazy(() => import('./pages/invoices/ReceivedInvoiceListPage'));
const ReceivedInvoiceDetailPage = lazy(() => import('./pages/invoices/ReceivedInvoiceDetailPage'));

// Clients / Fournisseurs
const ClientListPage = lazy(() => import('./pages/clients/ClientListPage'));
const ClientStatisticsPage = lazy(() => import('./pages/clients/ClientStatisticsPage'));
const ClientCreatePage = lazy(() => import('./pages/clients/ClientCreatePage'));
const ClientImportPage = lazy(() => import('./pages/clients/ClientImportPage'));
const ClientDetailPage = lazy(() => import('./pages/clients/ClientDetailPage'));
const SupplierListPage = lazy(() => import('./pages/suppliers/SupplierListPage'));
const SupplierStatisticsPage = lazy(() => import('./pages/suppliers/SupplierStatisticsPage'));

// Nomenclature (articles / produits)
const NomenclaturePage = lazy(() => import('./pages/nomenclature/NomenclaturePage'));
const NomenclatureCreatePage = lazy(() => import('./pages/nomenclature/NomenclatureCreatePage'));
const ArticleImportPage = lazy(() => import('./pages/nomenclature/ArticleImportPage'));
const ProductDetailPage = lazy(() => import('./pages/nomenclature/ProductDetailPage'));

// Connexions BDD
const ConnectionListPage = lazy(() => import('./pages/connections/ConnectionListPage'));
const ConnectionCreatePage = lazy(() => import('./pages/connections/ConnectionCreatePage'));
const ConnectionDetailPage = lazy(() => import('./pages/connections/ConnectionDetailPage'));

// Mappings
const MappingListPage = lazy(() => import('./pages/mappings/MappingListPage'));
const MappingDetailPage = lazy(() => import('./pages/mappings/MappingDetailPage'));

// Synchronisation
const SyncDashboardPage = lazy(() => import('./pages/sync/SyncDashboardPage'));

// Utilisateurs
const UserListPage = lazy(() => import('./pages/users/UserListPage'));
const UserCreatePage = lazy(() => import('./pages/users/UserCreatePage'));

// Paramètres
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));

// Profil
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const ChangePasswordPage = lazy(() => import('./pages/profile/ChangePasswordPage'));

// Notifications
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'));

// Aide
const HelpPage = lazy(() => import('./pages/help/HelpPage'));

// 404
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// ============================================================================
// App
// ============================================================================

// --- Emotion RTL cache ---
const ltrCache = createCache({ key: 'mui' });
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });

const App: React.FC = () => {
  const { theme } = useThemeMode();
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  // Créer un thème avec la direction RTL/LTR
  const dirTheme = useMemo(
    () => createTheme({ ...theme, direction: isRtl ? 'rtl' : 'ltr' }),
    [theme, isRtl]
  );

  return (
    <CacheProvider value={isRtl ? rtlCache : ltrCache}>
    <ThemeProvider theme={dirTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <CssBaseline />
      <BrowserRouter>
        <Suspense fallback={<Spinner fullScreen message="Chargement..." />}>
          <Routes>
            {/* ============================================================ */}
            {/* Routes publiques (AuthLayout) */}
            {/* ============================================================ */}
            <Route element={<PublicRoute />}>
              <Route element={<AuthLayout />}>
                <Route path={PUBLIC_ROUTES.LOGIN} element={<LoginPage />} />
                <Route path={PUBLIC_ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
                <Route path={PUBLIC_ROUTES.CREATE_ACCOUNT} element={<CreateAccountPage />} />
              </Route>
            </Route>

            {/* ============================================================ */}
            {/* Routes protégées — tous les rôles */}
            {/* ============================================================ */}
            <Route element={<ProtectedRoute />}>
              <Route element={<MainLayout />}>
                {/* Tableau de bord */}
                <Route path={PROTECTED_ROUTES.DASHBOARD} element={<DashboardPage />} />

                {/* Gestion stickers */}
                <Route path={PROTECTED_ROUTES.STICKERS} element={<StickersPage />} />

                {/* Factures / Reçus */}
                <Route path={PROTECTED_ROUTES.INVOICES} element={<InvoiceListPage />} />
                <Route path={PROTECTED_ROUTES.INVOICE_NEW} element={<InvoiceCreatePage />} />
                <Route path={PROTECTED_ROUTES.INVOICE_EDIT} element={<InvoiceCreatePage />} />
                <Route path={PROTECTED_ROUTES.INVOICE_REFUND} element={<InvoiceRefundPage />} />
                <Route path={PROTECTED_ROUTES.INVOICE_DETAIL} element={<InvoiceDetailPage />} />
                <Route path={PROTECTED_ROUTES.INVOICE_IMPORT} element={<InvoiceImportPage />} />
                <Route path={PROTECTED_ROUTES.INVOICES_RECEIVED} element={<ReceivedInvoiceListPage />} />
                <Route path={PROTECTED_ROUTES.INVOICES_RECEIVED_DETAIL} element={<ReceivedInvoiceDetailPage />} />

                {/* Clients / Fournisseurs */}
                <Route path={PROTECTED_ROUTES.CLIENTS} element={<ClientListPage />} />
                <Route path={PROTECTED_ROUTES.CLIENT_STATISTICS} element={<ClientStatisticsPage />} />
                <Route path={PROTECTED_ROUTES.CLIENT_NEW} element={<ClientCreatePage />} />
                <Route path={PROTECTED_ROUTES.CLIENT_IMPORT} element={<ClientImportPage />} />
                <Route path={PROTECTED_ROUTES.CLIENT_EDIT} element={<ClientCreatePage />} />
                <Route path={PROTECTED_ROUTES.CLIENT_DETAIL} element={<ClientDetailPage />} />
                <Route path={PROTECTED_ROUTES.SUPPLIERS} element={<SupplierListPage />} />
                <Route path={PROTECTED_ROUTES.SUPPLIER_STATISTICS} element={<SupplierStatisticsPage />} />
                <Route path={PROTECTED_ROUTES.SUPPLIER_IMPORT} element={<ClientImportPage />} />
                <Route path={PROTECTED_ROUTES.SUPPLIER_NEW} element={<ClientCreatePage />} />
                <Route path={PROTECTED_ROUTES.SUPPLIER_EDIT} element={<ClientCreatePage />} />
                <Route path={PROTECTED_ROUTES.SUPPLIER_DETAIL} element={<ClientDetailPage />} />

                {/* Nomenclature */}
                <Route path={PROTECTED_ROUTES.NOMENCLATURE} element={<NomenclaturePage />} />
                <Route path={PROTECTED_ROUTES.NOMENCLATURE_NEW} element={<NomenclatureCreatePage />} />
                <Route path={PROTECTED_ROUTES.NOMENCLATURE_IMPORT} element={<ArticleImportPage />} />
                <Route path={PROTECTED_ROUTES.NOMENCLATURE_EDIT} element={<NomenclatureCreatePage />} />
                <Route path={PROTECTED_ROUTES.NOMENCLATURE_DETAIL} element={<ProductDetailPage />} />

                {/* Aide */}
                <Route path={PROTECTED_ROUTES.HELP} element={<HelpPage />} />

                {/* Profil */}
                <Route path={PROTECTED_ROUTES.PROFILE} element={<ProfilePage />} />
                <Route path={PROTECTED_ROUTES.CHANGE_PASSWORD} element={<ChangePasswordPage />} />

                {/* Notifications */}
                <Route path={PROTECTED_ROUTES.NOTIFICATIONS} element={<NotificationsPage />} />

                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>

            {/* ============================================================ */}
            {/* Routes protégées — admin + manager */}
            {/* ============================================================ */}
            <Route element={<ProtectedRoute allowedRoles={['admin', 'manager']} />}>
              <Route element={<MainLayout />}>
                {/* Paramétrage */}
                <Route path={PROTECTED_ROUTES.SETTINGS} element={<SettingsPage />} />

                {/* Connexions BDD */}
                <Route path={PROTECTED_ROUTES.CONNECTIONS} element={<ConnectionListPage />} />
                <Route path={PROTECTED_ROUTES.CONNECTION_NEW} element={<ConnectionCreatePage />} />
                <Route path={PROTECTED_ROUTES.CONNECTION_DETAIL} element={<ConnectionDetailPage />} />

                {/* Mappings */}
                <Route path={PROTECTED_ROUTES.MAPPINGS} element={<MappingListPage />} />
                <Route path={PROTECTED_ROUTES.MAPPING_DETAIL} element={<MappingDetailPage />} />

                {/* Synchronisation */}
                <Route path={PROTECTED_ROUTES.SYNC} element={<SyncDashboardPage />} />
              </Route>
            </Route>

            {/* ============================================================ */}
            {/* Routes protégées — admin uniquement */}
            {/* ============================================================ */}
            <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
              <Route element={<MainLayout />}>
                {/* Gestion utilisateurs */}
                <Route path={PROTECTED_ROUTES.USERS} element={<UserListPage />} />
                <Route path={PROTECTED_ROUTES.USER_NEW} element={<UserCreatePage />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>

      {/* Snackbar global */}
      <GlobalSnackbar />
    </LocalizationProvider>
    </ThemeProvider>
    </CacheProvider>
  );
};

export default App;
